/**
 * Home (메인/문서/컴포넌트 쇼케이스 등) – PHP HomeController 와 액션명 동기화.
 */
import { Logger } from "../../utils/Logger";
import { Modal } from "../../utils/Modal";
import { Form } from "../../core/Form";
import { ContactFormController } from "../components/ContactForm";
import { QuillEditor } from "../../components/QuillEditor";
import { initMainFullPage } from "../components/MainFullPage";
import { initCompanyPossibility } from "../components/CompanyPossibility";
import { PortfolioModal } from "../components/PortfolioModal";

type SwiperCtor = new (
    el: Element,
    options: Record<string, unknown>,
) => {
    params: Record<string, unknown>;
};

type LenisLike = {
    scrollTo: (
        target: number | string | HTMLElement,
        opts?: {
            offset?: number;
            duration?: number;
            immediate?: boolean;
        },
    ) => void;
};

function getWindowLenis(): LenisLike | undefined {
    return (window as unknown as { lenis?: LenisLike }).lenis;
}

/** 앵커 이동: Lenis가 있으면 동일한 easing으로 스크롤, scroll-margin-top 반영 */
function scrollToAnchorElement(el: HTMLElement, reduceMotion: boolean): void {
    if (reduceMotion) {
        el.scrollIntoView({ behavior: "auto", block: "start" });
        return;
    }
    const lenis = getWindowLenis();
    const sm = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
    if (lenis?.scrollTo) {
        lenis.scrollTo(el, { offset: -sm, duration: 1.15 });
        return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function initSubpageHeroScrollHints(): void {
    const hints = Array.from(
        document.querySelectorAll<HTMLAnchorElement>(".no-main__scroll-hint[href^='#']"),
    );
    if (!hints.length) return;

    const reduceMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    hints.forEach((hint) => {
        hint.addEventListener("click", (e) => {
            const href = hint.getAttribute("href") ?? "";
            const hash = href.startsWith("#") ? href.slice(1) : "";
            if (!hash) return;

            const target = document.getElementById(hash);
            if (!target) return;

            e.preventDefault();
            scrollToAnchorElement(target, reduceMotion);

            if (window.history?.replaceState) {
                try {
                    window.history.replaceState(null, "", `#${hash}`);
                } catch {
                    /* ignore */
                }
            }
        });
    });
}

function getCompanyMessages(): Record<string, string> {
    const lang = document.documentElement.lang || "ko";
    const isKo = lang.startsWith("ko");
    return {
        nameRequired: isKo ? "이름을 입력해주세요." : "Please enter your name.",
        companyRequired: isKo
            ? "회사명을 입력해주세요."
            : "Please enter your company.",
        deptRequired: isKo
            ? "부서명을 입력해주세요."
            : "Please enter your department.",
        categoryRequired: isKo
            ? "서비스를 선택해주세요."
            : "Please select a category.",
        phoneRequired: isKo
            ? "전화번호를 입력해주세요."
            : "Please enter your phone number.",
        emailRequired: isKo
            ? "이메일을 입력해주세요."
            : "Please enter your email.",
        messageRequired: isKo
            ? "내용을 입력해주세요."
            : "Please enter your message.",
        privacyRequired: isKo
            ? "개인정보 수집 및 이용에 동의해주세요."
            : "Please agree to the privacy policy.",
        captchaRequired: isKo
            ? "보안코드를 입력해주세요."
            : "Please enter the security code.",
        requestFailed: isKo ? "전송에 실패했습니다." : "Request failed.",
        errorOccurred: isKo ? "오류가 발생했습니다." : "An error occurred.",
    };
}

function initCompanyProjectSwiper(): void {
    const Swiper = (
        typeof window !== "undefined"
            ? (window as unknown as { Swiper?: SwiperCtor }).Swiper
            : undefined
    ) as SwiperCtor | undefined;
    if (!Swiper) return;

    const el = document.querySelector(".bro-company__project-swiper");
    if (!el) return;

    const prevEl = document.querySelector(".bro-company__project-btn--prev");
    const nextEl = document.querySelector(".bro-company__project-btn--next");

    try {
        new Swiper(el, {
            slidesPerView: "auto",
            spaceBetween: 20,
            watchOverflow: true,
            centeredSlides: false,
            slidesOffsetBefore: 0,
            slidesOffsetAfter: 0,
            navigation:
                prevEl && nextEl
                    ? {
                          prevEl,
                          nextEl,
                      }
                    : undefined,
            breakpoints: {
                0: { spaceBetween: 16 },
                768: { spaceBetween: 24 },
            },
        });
    } catch (e) {
        Logger.error("company swiper", e);
    }
}

function initNewsDetailRelatedSwiper(): void {
    const Swiper = (
        typeof window !== "undefined"
            ? (window as unknown as { Swiper?: SwiperCtor }).Swiper
            : undefined
    ) as SwiperCtor | undefined;
    if (!Swiper) return;

    const el = document.querySelector(".bro-news-detail__related-swiper");
    if (!el) return;

    const prevEl = document.querySelector(
        ".bro-news-detail__related-nav--prev",
    );
    const nextEl = document.querySelector(
        ".bro-news-detail__related-nav--next",
    );

    try {
        new Swiper(el, {
            slidesPerView: 1,
            spaceBetween: 12,
            watchOverflow: true,
            navigation:
                prevEl && nextEl
                    ? {
                          prevEl,
                          nextEl,
                      }
                    : undefined,
            breakpoints: {
                544: {
                    slidesPerView: 2,
                    spaceBetween: 14,
                },
                1024: {
                    slidesPerView: 3,
                    spaceBetween: 16,
                },
            },
        });
    } catch (e) {
        Logger.error("news detail related swiper", e);
    }
}

function initCompanyPortfolioModal(): void {
    const root = document.querySelector<HTMLElement>(
        "[data-bro-portfolio-modal]",
    );
    if (!root || !document.querySelector("[data-bro-portfolio-open]")) {
        return;
    }
    try {
        new PortfolioModal(root);
    } catch (e) {
        Logger.error("company portfolio modal", e);
    }
}

function initCompanyClientTabs(): void {
    const tabs = document.querySelectorAll<HTMLButtonElement>("[data-bro-tab]");
    if (!tabs.length) return;

    const panels = document.querySelectorAll<HTMLElement>("[data-bro-panel]");
    const activate = (id: string): void => {
        tabs.forEach((btn) => {
            const active = btn.getAttribute("data-bro-tab") === id;
            btn.classList.toggle("bro-company__tab--active", active);
            btn.setAttribute("aria-selected", active ? "true" : "false");
        });
        panels.forEach((panel) => {
            const pid = panel.getAttribute("data-bro-panel");
            const show = pid === id;
            panel.classList.toggle("is-hidden", !show);
            panel.setAttribute("aria-hidden", show ? "false" : "true");
        });
    };

    tabs.forEach((btn) => {
        btn.addEventListener("click", () => {
            const id = btn.getAttribute("data-bro-tab");
            if (id) activate(id);
        });
    });
}

function validateCompanyInquiry(form: HTMLFormElement): string | null {
    const m = getCompanyMessages();
    const name = (
        form.querySelector<HTMLInputElement>('[name="name"]')?.value ?? ""
    ).trim();
    const company = (
        form.querySelector<HTMLInputElement>('[name="company"]')?.value ?? ""
    ).trim();
    const department = (
        form.querySelector<HTMLInputElement>('[name="department"]')?.value ?? ""
    ).trim();
    const categorySel =
        form.querySelector<HTMLSelectElement>('[name="category"]');
    const categoryVal = (categorySel?.value ?? "").trim();
    const categoryLabel =
        categorySel && categorySel.selectedIndex >= 0
            ? (
                  categorySel.options[categorySel.selectedIndex]?.text ?? ""
              ).trim()
            : "";
    const phone = (
        form.querySelector<HTMLInputElement>('[name="phone"]')?.value ?? ""
    ).trim();
    const email = (
        form.querySelector<HTMLInputElement>('[name="email"]')?.value ?? ""
    ).trim();
    const message = (
        form.querySelector<HTMLTextAreaElement>('[name="message"]')?.value ?? ""
    ).trim();
    const privacy = form.querySelector<HTMLInputElement>(
        '[name="privacy_agree"]',
    )?.checked;
    const rCaptcha = (
        form.querySelector<HTMLInputElement>('[name="r_captcha"]')?.value ?? ""
    ).trim();

    if (!name) return m.nameRequired;
    if (!company) return m.companyRequired;
    if (!department) return m.deptRequired;
    if (!categoryVal) return m.categoryRequired;
    if (!phone) return m.phoneRequired;
    if (!email) return m.emailRequired;
    if (!message) return m.messageRequired;
    if (!privacy) return m.privacyRequired;
    if (!rCaptcha) return m.captchaRequired;

    const contents = form.querySelector<HTMLInputElement>('[name="contents"]');
    if (contents) {
        const typeLine =
            categoryLabel !== ""
                ? `문의 유형: ${categoryLabel} (${categoryVal})`
                : `문의 유형: ${categoryVal}`;
        contents.value = [
            `회사: ${company}`,
            `부서: ${department}`,
            typeLine,
            `전화: ${phone}`,
            "",
            message,
        ].join("\n");
    }

    return null;
}

function refreshCompanyCaptcha(form: HTMLFormElement): void {
    const url = form.dataset.captchaUrl;
    if (!url) return;
    const img = form.querySelector<HTMLImageElement>("#company-captcha-img");
    if (img) img.src = `${url}?v=${Date.now()}`;
}

function initTeamPage(): void {
    const root = document.querySelector(".bro-team");
    if (!root) return;

    const cards = root.querySelectorAll<HTMLElement>("[data-team-card]");
    const search = root.querySelector<HTMLInputElement>("[data-team-search]");
    const companySel = root.querySelector<HTMLSelectElement>(
        "[data-team-filter-company]",
    );
    const positionSel = root.querySelector<HTMLSelectElement>(
        "[data-team-filter-position]",
    );
    const empty = root.querySelector<HTMLElement>("[data-team-empty]");

    const apply = (): void => {
        const q = (search?.value ?? "").toLowerCase().trim();
        const co = companySel?.value ?? "";
        const pos = positionSel?.value ?? "";
        let visible = 0;

        cards.forEach((card) => {
            const hay = (card.dataset.search ?? "").toLowerCase();
            const matchSearch = !q || hay.includes(q);
            const cardCo = (card.dataset.teamCompany ?? "").trim();
            const cardPos = (card.dataset.teamPosition ?? "").trim();
            const matchCompany = !co || cardCo === co;
            const matchPosition = !pos || cardPos === pos;
            const show = matchSearch && matchCompany && matchPosition;
            const cell = card.closest<HTMLElement>(".bro-team__cell") ?? card;
            cell.classList.toggle("is-hidden", !show);
            if (show) visible += 1;
        });

        if (empty) {
            empty.hidden = visible > 0;
        }
    };

    search?.addEventListener("input", apply);
    companySel?.addEventListener("change", apply);
    positionSel?.addEventListener("change", apply);

    apply();
}

function initPortfolioPage(): void {
    const root = document.querySelector(".bro-portfolio");
    if (!root) return;

    const cards = root.querySelectorAll<HTMLElement>("[data-portfolio-card]");
    const search = root.querySelector<HTMLInputElement>(
        "[data-portfolio-search]",
    );
    const industrySel = root.querySelector<HTMLSelectElement>(
        "[data-portfolio-filter-industry]",
    );
    const yearSel = root.querySelector<HTMLSelectElement>(
        "[data-portfolio-filter-year]",
    );
    const empty = root.querySelector<HTMLElement>("[data-portfolio-empty]");

    const apply = (): void => {
        const q = (search?.value ?? "").toLowerCase().trim();
        const ind = industrySel?.value ?? "";
        const y = yearSel?.value ?? "";
        let visible = 0;

        cards.forEach((card) => {
            const matchInd = !ind || (card.dataset.industry ?? "") === ind;
            const matchYear = !y || String(card.dataset.year ?? "") === y;
            const hay = (card.dataset.search ?? "").toLowerCase();
            const matchSearch = !q || hay.includes(q);
            const show = matchInd && matchYear && matchSearch;
            card.classList.toggle("is-hidden", !show);
            if (show) visible += 1;
        });

        if (empty) {
            empty.hidden = visible > 0;
        }
    };

    search?.addEventListener("input", apply);
    industrySel?.addEventListener("change", apply);
    yearSel?.addEventListener("change", apply);

    apply();
}

function initServicesIndexNav(): void {
    const navRoot = document.querySelector<HTMLElement>(
        ".bro-services-lib__tabs",
    );
    if (!navRoot) return;

    const sections = Array.from(
        document.querySelectorAll<HTMLElement>(
            ".bro-services-lib__section[id]",
        ),
    );
    const controls = navRoot.querySelectorAll<HTMLElement>(
        "[data-bro-services-nav]",
    );

    if (!sections.length || !controls.length) return;

    const getSid = (el: HTMLElement): string =>
        el.getAttribute("data-bro-services-nav") ?? "";

    const prefersReducedMotion = (): boolean =>
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const setActive = (sid: string): void => {
        const has = sid !== "";
        controls.forEach((el) => {
            const on = has && getSid(el) === sid;
            el.classList.toggle("is-active", on);
            if (el instanceof HTMLAnchorElement) {
                if (on) {
                    el.setAttribute("aria-current", "true");
                } else {
                    el.removeAttribute("aria-current");
                }
            }
        });
    };

    const sidFromHash = (): string => {
        const h = window.location.hash.replace(/^#/, "");
        if (!h) return "";
        const el = document.getElementById(h);
        if (el?.classList.contains("bro-services-lib__section")) {
            return h;
        }
        return "";
    };

    const applyFromHash = (): void => {
        setActive(sidFromHash());
    };

    controls.forEach((el) => {
        el.addEventListener("click", (e) => {
            const sid = getSid(el);
            if (!sid) return;
            const section = sections.find((s) => s.id === sid);
            if (!section) return;
            e.preventDefault();
            scrollToAnchorElement(section, prefersReducedMotion());
            setActive(sid);
            if (window.history?.replaceState) {
                try {
                    window.history.replaceState(null, "", `#${sid}`);
                } catch {
                    /* ignore */
                }
            }
        });
    });

    window.addEventListener("hashchange", () => {
        applyFromHash();
    });

    applyFromHash();
}

type ScrollTriggerLite = {
    create: (vars: Record<string, unknown>) => { kill: () => void };
    refresh: () => void;
};

function getSiteHeaderOffsetPx(): number {
    const el = document.querySelector<HTMLElement>(".no-header");
    if (el) return Math.round(el.getBoundingClientRect().height);
    return 72;
}

/** Services 목록: 탭 레일을 헤더 아래에 ScrollTrigger pin (Lenis 시 scroller 일치) */
function initServicesTabsRailPin(): void {
    const rail = document.querySelector<HTMLElement>(
        ".bro-services-lib__tabs-rail",
    );
    const main = document.querySelector<HTMLElement>(".bro-services-lib__main");
    if (!rail || !main || rail.dataset.broServicesStPin === "1") return;

    const gsap = (
        window as typeof window & {
            gsap?: { registerPlugin: (p: unknown) => void };
        }
    ).gsap;
    const ST = (window as typeof window & { ScrollTrigger?: ScrollTriggerLite })
        .ScrollTrigger;
    if (!gsap || !ST) return;

    gsap.registerPlugin(ST);

    const scroller = (window as typeof window & { lenis?: unknown }).lenis
        ? document.documentElement
        : undefined;

    const headerPx = (): number => getSiteHeaderOffsetPx();

    const vars: Record<string, unknown> = {
        trigger: rail,
        start: () => `top top+=${headerPx()}`,
        endTrigger: main,
        end: () => `bottom top+=${headerPx()}`,
        pin: true,
        pinSpacing: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
    };
    if (scroller) vars.scroller = scroller;

    ST.create(vars);
    rail.dataset.broServicesStPin = "1";

    const refreshSt = (): void => {
        ST.refresh();
    };
    requestAnimationFrame(() => {
        refreshSt();
        requestAnimationFrame(refreshSt);
    });
    window.addEventListener("load", refreshSt, { once: true });
}

function initServiceItemToc(): void {
    const sections = Array.from(
        document.querySelectorAll<HTMLElement>("[data-svc-section]"),
    );
    const links =
        document.querySelectorAll<HTMLAnchorElement>("[data-svc-toc]");
    if (!links.length) return;

    const getHash = (link: HTMLAnchorElement): string => {
        const href = link.getAttribute("href") ?? "";
        const idx = href.indexOf("#");
        return idx >= 0 ? href.slice(idx + 1) : "";
    };

    const dotItems =
        document.querySelectorAll<HTMLElement>("[data-svc-toc-dot]");

    const setActive = (id: string): void => {
        links.forEach((a) => {
            a.classList.toggle("is-active", getHash(a) === id);
        });
        dotItems.forEach((el) => {
            const sid = el.getAttribute("data-svc-toc-dot") ?? "";
            el.classList.toggle("is-active", sid === id);
        });
    };

    const reduceMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let spyPausedUntil = 0;

    const updateActiveFromScroll = (): void => {
        if (performance.now() < spyPausedUntil) return;
        if (!sections.length) return;
        const header = document.querySelector<HTMLElement>(".no-header");
        const headerH = header?.offsetHeight ?? 0;
        const line = window.scrollY + headerH + 10;

        let currentId = sections[0].id;
        for (const s of sections) {
            if (!s.id) continue;
            const top = s.getBoundingClientRect().top + window.scrollY;
            if (top <= line) currentId = s.id;
        }

        const maxScroll =
            document.documentElement.scrollHeight - window.innerHeight;
        if (window.scrollY >= maxScroll - 8 && sections.length) {
            currentId = sections[sections.length - 1].id;
        }
        setActive(currentId);
    };

    let ticking = false;
    const onScrollOrResize = (): void => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
            ticking = false;
            updateActiveFromScroll();
        });
    };
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize, { passive: true });
    updateActiveFromScroll();

    links.forEach((a) => {
        a.addEventListener("click", (e) => {
            const hash = getHash(a);
            if (!hash) return;
            const el = document.getElementById(hash);
            if (!el) return;
            e.preventDefault();
            spyPausedUntil = performance.now() + (reduceMotion ? 50 : 1300);
            setActive(hash);
            scrollToAnchorElement(el, reduceMotion);
            if (window.history && window.history.replaceState) {
                try {
                    window.history.replaceState(null, "", `#${hash}`);
                } catch {
                    // Some browser environments can block history updates.
                }
            }
            /* 클릭 후 링크에 포커스가 남으면 :focus-within으로 TOC가 펼친 채로 고정됨 */
            queueMicrotask(() => {
                a.blur();
            });
        });
    });

    dotItems.forEach((btn) => {
        btn.addEventListener("click", (e) => {
            e.preventDefault();
            const sid = btn.getAttribute("data-svc-toc-dot") ?? "";
            if (!sid) return;
            const el = document.getElementById(sid);
            if (!el) return;
            spyPausedUntil = performance.now() + (reduceMotion ? 50 : 1300);
            setActive(sid);
            scrollToAnchorElement(el, reduceMotion);
            if (window.history && window.history.replaceState) {
                try {
                    window.history.replaceState(null, "", `#${sid}`);
                } catch {
                    /* ignore */
                }
            }
            queueMicrotask(() => {
                btn.blur();
            });
        });
    });

    const initial = window.location.hash
        ? window.location.hash.replace(/^#/, "")
        : "";
    if (initial) setActive(initial);
}

function applyCompanyServiceQueryToCategory(): void {
    const form = document.getElementById(
        "company-contact-form",
    ) as HTMLFormElement | null;
    if (!form) return;
    const service = new URLSearchParams(window.location.search)
        .get("service")
        ?.trim();
    if (!service) return;
    const sel =
        form.querySelector<HTMLSelectElement>("#company-inquiry-category") ??
        form.querySelector<HTMLSelectElement>('[name="category"]');
    if (!sel) return;
    const has = Array.from(sel.options).some((o) => o.value === service);
    if (has) {
        sel.value = service;
    }
}

function initCompanyInquiryForm(): void {
    const form = document.getElementById(
        "company-contact-form",
    ) as HTMLFormElement | null;
    if (!form) return;

    const submitUrl = form.dataset.submitUrl;
    if (!submitUrl) return;

    applyCompanyServiceQueryToCategory();

    const m = getCompanyMessages();

    const f = new Form(form, {
        submitUrl,
        validate: validateCompanyInquiry,
        loaderSelector: ".company-form-btn-loader",
        requestFailedMessage: m.requestFailed,
        invalidResponseMessage: m.errorOccurred,
        onSuccess: (data) => {
            const msg = (data?.msg ??
                data?.message ??
                m.errorOccurred) as string;
            void Modal.alert(msg);
            form.reset();
            refreshCompanyCaptcha(form);
        },
        onFail: (message: string) => {
            void Modal.alert(message || m.errorOccurred);
            refreshCompanyCaptcha(form);
        },
    });
    f.init();

    const img = form.querySelector<HTMLImageElement>("#company-captcha-img");
    if (img) {
        img.addEventListener("click", () => refreshCompanyCaptcha(form));
        img.style.cursor = "pointer";
    }
    const refreshBtn = document.getElementById("company-captcha-refresh");
    if (refreshBtn) {
        refreshBtn.addEventListener("click", () => refreshCompanyCaptcha(form));
    }
}

export const HomeController = {
    index(): void {
        initMainFullPage();
    },

    company(): void {
        Logger.info("HomeController.company");
        initSubpageHeroScrollHints();
        initCompanyPossibility();
        initCompanyProjectSwiper();
        initCompanyPortfolioModal();
        initCompanyClientTabs();
        initCompanyInquiryForm();
    },

    services(): void {
        Logger.info("HomeController.services");
        initSubpageHeroScrollHints();
        initServicesIndexNav();
    },

    serviceItemDetail(): void {
        initServiceItemToc();

        void import("../components/ServiceItemHeroWave").then(
            ({ initServiceItemHeroWave }) => {
                document
                    .querySelectorAll<HTMLCanvasElement>(
                        "[data-bro-svc-hero-wave]",
                    )
                    .forEach((canvas) => {
                        initServiceItemHeroWave(canvas);
                    });
            },
        );
    },

    team(): void {
        Logger.info("HomeController.team");
        initSubpageHeroScrollHints();
        initTeamPage();
    },

    newsInsights(): void {
        Logger.info("HomeController.newsInsights");
        initSubpageHeroScrollHints();
    },

    newsInsightDetail(): void {
        Logger.info("HomeController.newsInsightDetail");
        initNewsDetailRelatedSwiper();
    },

    portfolio(): void {
        Logger.info("HomeController.portfolio");
        initPortfolioPage();
    },

    portfolioDetail(): void {
        Logger.info("HomeController.portfolioDetail");
    },

    contact(): void {
        Logger.info("HomeController.contact");
        if (document.getElementById("company-contact-form")) {
            initCompanyInquiryForm();
        } else {
            ContactFormController.form();
        }
    },

    components(): void {
        Logger.info("HomeController.components");
        const csrf =
            (
                document.querySelector(
                    'input[name="_token"]',
                ) as HTMLInputElement | null
            )?.value ??
            (
                document.querySelector(
                    'meta[name="csrf-token"]',
                ) as HTMLMetaElement | null
            )?.content ??
            "";
        const editorEl = document.getElementById("components-content-editor");
        const thumbWrap = document.getElementById("components-thumb-upload");
        const uploadUrl = thumbWrap?.getAttribute("data-upload-url") ?? "";

        if (editorEl && uploadUrl && csrf) {
            const placeholder = editorEl.getAttribute("data-placeholder") ?? "";
            const quill = new QuillEditor(editorEl, {
                placeholder,
                upload: { uploadUrl, csrf, fileKey: "file" },
            });
            quill.init();
        } else if (editorEl) {
            const placeholder = editorEl.getAttribute("data-placeholder") ?? "";
            const quill = new QuillEditor(editorEl, { placeholder });
            quill.init();
        }

        const thumbDrop = document.getElementById("components-thumb-dropzone");
        const thumbInput = document.getElementById(
            "components-thumb-input",
        ) as HTMLInputElement | null;
        const thumbPreview = document.getElementById(
            "components-thumb-preview",
        );
        if (uploadUrl && thumbInput && thumbDrop && thumbPreview && csrf) {
            const uploadThumb = (file: File): void => {
                const fd = new FormData();
                fd.append("file", file);
                fd.append("_token", csrf);
                fd.append("category", "image");
                fd.append("target_type", "post");
                fd.append("target_id", "0");
                fetch(uploadUrl, {
                    method: "POST",
                    body: fd,
                    headers: {
                        "X-Requested-With": "XMLHttpRequest",
                        Accept: "application/json",
                    },
                })
                    .then((r) => r.json())
                    .then((json: Record<string, unknown>) => {
                        const data = json?.data as
                            | Record<string, unknown>
                            | undefined;
                        if (json?.result === "success" && data?.file_url) {
                            thumbPreview.innerHTML =
                                '<img src="' +
                                String(data.file_url).replace(/"/g, "&quot;") +
                                '" alt="" style="max-width:200px;height:auto;border-radius:4px;">';
                        } else {
                            void Modal.alert(
                                (json?.msg as string) || "업로드 실패",
                            );
                        }
                    })
                    .catch(() => void Modal.alert("업로드 중 오류"));
            };
            thumbDrop.addEventListener("click", () => thumbInput.click());
            thumbInput.addEventListener("change", () => {
                const file = thumbInput.files?.[0];
                thumbInput.value = "";
                if (file?.type?.startsWith("image/")) uploadThumb(file);
            });
        }
    },
};
